import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Merge conditional class names and resolve conflicting Tailwind utilities. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

const EMPTY = '\u2014'; // em dash

/**
 * Format an ISO timestamp for display, or an em dash when unavailable.
 * Pass the active locale so dates follow the interface language (and use the
 * right calendar/numerals direction in RTL locales).
 */
export function formatDateTime(value: string | null | undefined, locale = 'en'): string {
  if (!value) return EMPTY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  try {
    return date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return date.toLocaleString('en', { dateStyle: 'medium', timeStyle: 'short' });
  }
}

/** Best-effort human name for a user (profile name, else email local part). */
export function displayNameOf(user: { email: string; profile?: { displayName: string | null } | null }): string {
  const explicit = user.profile?.displayName?.trim();
  if (explicit) return explicit;
  return user.email.split('@')[0];
}

/** Up to two uppercase initials derived from an email address. */
export function initialsOf(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  const letters = (parts.length > 1 ? parts[0][0] + parts[1][0] : local.slice(0, 2)) || '?';
  return letters.toUpperCase();
}
